import { type FC } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Upload, FolderOpen, LogOut, UserX, ExternalLink, Users } from "lucide-react";
import { useAuthStore } from "../../store/auth-store.js";
import { cn } from "../../lib/utils.js";

const DEVICE_FARM_URL =
  import.meta.env.VITE_CONTENT_UPLOADER_DEVICE_FARM_URL ?? "/";

export const Header: FC = () => {
  const { user, org, logout, deleteAccount } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const canManageCreators =
    user?.v1Role === "AGENCY_OWNER" ||
    user?.v1Role === "ACCOUNT_MANAGER" ||
    user?.role === "ADMIN";

  const navItems = [
    { to: "/", label: "Upload", icon: Upload },
    { to: "/content", label: "Content", icon: FolderOpen },
    ...(canManageCreators ? [{ to: "/creators", label: "Creators", icon: Users }] : []),
  ];

  const handleLogout = async () => {
    await logout();
    queryClient.clear();
    navigate("/login", { replace: true });
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      "This will permanently delete your account and revoke active sessions. Type DELETE MY ACCOUNT to continue?",
    );
    if (!confirmed) return;

    const typed = window.prompt(
      "Type DELETE MY ACCOUNT to confirm account deletion",
    );
    if (typed !== "DELETE MY ACCOUNT") {
      return;
    }

    try {
      await deleteAccount(typed);
      queryClient.clear();
      navigate("/login", { replace: true });
    } catch {
      // error is surfaced in the store
    }
  };

  const displayRole = user?.v1Role ?? user?.role ?? null;

  return (
    <header className="sticky top-0 z-50 border-b border-slate-800 bg-slate-950/80 backdrop-blur-md">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <Upload className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-semibold text-slate-100">
              Content Uploader
            </span>
          </div>

          <nav className="flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
                    isActive
                      ? "bg-slate-800 text-slate-100"
                      : "text-slate-500 hover:bg-slate-800/50 hover:text-slate-300",
                  )
                }
              >
                <item.icon className="h-3.5 w-3.5" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <a
            href={DEVICE_FARM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-slate-500 transition-colors hover:bg-slate-800/50 hover:text-slate-300"
          >
            <ExternalLink className="h-3 w-3" />
            Laxmi AI
          </a>

          <div className="h-4 w-px bg-slate-800" />

          <div className="flex items-center gap-2">
            <div className="text-right">
              <p className="text-xs font-medium text-slate-300">
                {user?.name ?? user?.email}
              </p>
              {displayRole !== null && (
                <p className="text-[10px] font-medium uppercase tracking-wide text-blue-400/80">
                  {displayRole.replace(/_/g, " ")}
                </p>
              )}
              {org !== null && (
                <p className="text-[10px] text-slate-600">{org.name}</p>
              )}
            </div>
            <button
              onClick={handleLogout}
              className="flex h-8 items-center gap-1.5 rounded-lg border border-slate-800 px-2.5 text-xs font-medium text-slate-400 transition-colors hover:border-red-900/60 hover:bg-red-950/40 hover:text-red-400"
              title="Sign out"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
            <button
              onClick={handleDeleteAccount}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-800/50 hover:text-red-400"
              title="Delete account"
            >
              <UserX className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};
